/**
 * May this link stand behind "Our pick" / "Order Now" on a page for this
 * market? Shared by presentProduct (the product's own link and its offers) and
 * the offers panel, and by the /go hop for the parameter strip.
 *
 * Why (QA 2026-09-21 to 09-25, `trust-buy-buttons-open-wrong-destination`):
 * buy buttons on Canadian and UK pages opened shopee.ph (a lightsaber rack for
 * a sensor mount), walmart.com and amazon.com on en-CA, billiger.de on en-UK,
 * a Starkey brochure PDF, a Phonak product-information PDF, a Unox user
 * manual, the pro portals pro.resound.com and widexpro.com, and an eBay link
 * carrying another company's eBay Partner Network tag. The Amazon verdict gate
 * covered only judged Amazon links; every partner link went out ungated.
 *
 * A link fails when:
 *  - it is not a product page (buy-link-quality's nonShopWord: a PDF, a
 *    manual, a brochure, an archive or brand page, a pro or partner portal,
 *    a home page, a help section like /supportpages/);
 *  - its store does not serve the page's market: an Amazon marketplace other
 *    than the market's, a country domain of another country, a locale in the
 *    URL naming another country (/eu/ names the EU), or a generic domain
 *    (.com) with no sign of this market, unless it is a known local retailer
 *    or its page quoted a price in the market's currency (a bare /fr/, /de/
 *    or fr. is a sign for France or Germany only). The maker's own site gets
 *    no exemption since 2026-09-28: jabraenhance.com sells in the US only;
 *  - (when the product is given) its page names another product: another
 *    model of the line, or a descriptive slug that names neither the brand
 *    nor a model code of the product.
 *
 * Pure and client-safe. Never throws.
 */

import TRACKING_HOSTS from "../generated/tracking-hosts.json";
import { hostParts, isLocaleSegment, linkNamesOtherModel, isModelCode, nonShopWord, productTokens } from "../server/products/buy-link-quality";
import { getAmazonDomainForRegion } from "../types/amazon.type";
import { REGION_FOR_CODE, type RegionCode } from "../types/region.type";
import { normalizeMerchantDomain } from "./offers/merchant-domain";
import { lookupOfferMerchant } from "./offers/rank-offers";

/* ------------------------------------------------------------------ *
 * Hosts
 * ------------------------------------------------------------------ */

function parse(url: string | null | undefined): URL | null {
  if (typeof url !== "string" || !url.trim()) return null;
  try {
    return new URL(url.trim());
  } catch {
    return null;
  }
}

function bareHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
}

function isAmazonMarketHost(host: string): boolean {
  return /(^|\.)amazon\.[a-z.]+$/.test(host);
}

/** amzn.to, a.co and amzn.eu name no marketplace; they are left alone. */
function isAmazonShortHost(host: string): boolean {
  return host === "amzn.to" || host === "a.co" || host === "amzn.eu";
}

/** Affiliate network click hosts (ours, from merchant_programs): never judged by market. */
const TRACKING_HOST_SET: ReadonlySet<string> = new Set(
  Object.values(TRACKING_HOSTS as Record<string, readonly string[]>)
    .flat()
    .map((host) => bareHost(host)),
);

/* ------------------------------------------------------------------ *
 * Markets
 * ------------------------------------------------------------------ */

/**
 * The country codes (as they appear in a domain or a URL locale) whose stores
 * serve each market. Ireland shops UK stores and Austria German ones, the
 * same neighbours the Amazon marketplace map already uses.
 */
const MARKET_COUNTRIES: Readonly<Record<string, readonly string[]>> = {
  CA: ["ca"],
  US: ["us"],
  FR: ["fr"],
  UK: ["uk", "gb"],
  AU: ["au"],
  BR: ["br"],
  PT: ["pt"],
  DE: ["de"],
  ES: ["es"],
  IT: ["it"],
  MX: ["mx"],
  CH: ["ch"],
  AT: ["at", "de"],
  BE: ["be"],
  NL: ["nl"],
  PL: ["pl"],
  SE: ["se"],
  IE: ["ie", "uk", "gb"],
  NZ: ["nz"],
  IN: ["in"],
  AR: ["ar"],
  CO: ["co"],
  CL: ["cl"],
  JP: ["jp"],
};

const EU_MARKETS = new Set(["FR", "DE", "ES", "IT", "PT", "AT", "BE", "NL", "PL", "SE", "IE"]);

/** Two-letter domains sold as generic names: they say nothing about a country. */
const GENERIC_TWO_LETTER_TLDS = new Set([
  "io", "ai", "me", "tv", "co", "ly", "gg", "fm", "to", "cc", "ws", "sh", "ac", "la", "vc", "gl", "so",
]);

/** Language codes that are also country codes: a bare `/fr/` or `/de/` is a language, not a market. */
const LANGUAGE_CODES = new Set(["en", "fr", "de", "es", "it", "pt", "nl", "pl", "sv", "ja", "zh", "da", "fi", "no", "ko", "ru"]);

/**
 * The country whose shops mostly use a language, for the languages of our
 * markets. A bare `/fr/` path or `fr.` subdomain says France (se.com/fr/fr,
 * fr.jbl.com), but not Quebec, whose shops use /fr/ too; `/es/` says Spain,
 * not Mexico or the Spanish pages of a US store; `/ja/` says Japan.
 */
const LANGUAGE_HOME_COUNTRY: Readonly<Record<string, string>> = {
  fr: "fr", de: "de", es: "es", it: "it", nl: "nl", pl: "pl", pt: "pt", sv: "se", ja: "jp",
};

/** Country codes that, as a bare URL segment or subdomain, name a market. */
const BARE_COUNTRY_MARKERS = new Set([
  "ca", "us", "uk", "gb", "au", "nz", "ie", "ch", "at", "be", "mx", "br", "ar", "cl", "jp", "se",
]);

/** Every country a locale in a URL may name: ours, and the ones stores we met are in. */
const KNOWN_COUNTRIES = new Set([
  ...Object.values(MARKET_COUNTRIES).flat(),
  // The EU as a whole: /eu/, eu., en-eu (uswww.ezvizlife.com/eu, 2026-09-28).
  "eu",
  "ph", "cn", "sg", "my", "hk", "tw", "kr", "ru", "dk", "no", "fi", "cz", "hu", "ro", "gr", "tr",
  "za", "ae", "sa", "il", "th", "vn", "id", "pe", "ve", "ec", "uy", "lu", "sk", "si", "hr", "bg",
]);

/**
 * Local retailers on a generic domain that the reviewed-merchant registry
 * does not list yet. The registry answers first; this only widens it.
 */
const LOCAL_GENERIC_RETAILERS: Readonly<Record<string, readonly string[]>> = {
  CA: [
    "canadacomputers.com", "memoryexpress.com", "thebay.com", "londondrugs.com", "golftown.com",
    "thebrick.com", "princessauto.com", "marks.com", "structube.com", "leevalley.com",
    "healthyplanetcanada.com", "mastermindtoys.com",
    // Quebec shops on generic domains (review of 2026-09-25).
    "bureauengros.com", "renaud-bray.com", "bmr.co", "patrickmorin.com",
  ],
  UK: [
    "johnlewis.com", "ao.com", "boots.com", "screwfix.com", "toolstation.com", "diy.com",
    "superdrug.com", "richersounds.com", "ebuyer.com", "halfords.com", "sportsdirect.com",
    "hollandandbarrett.com", "dunelm.com", "marksandspencer.com",
    "lookfantastic.com", "sigmasports.com", "waitrose.com", "zavvi.com",
  ],
  FR: ["ldlc.com", "materiel.net"],
};

function hostMatches(host: string, domain: string): boolean {
  return host === domain || host.endsWith(`.${domain}`);
}

/** The country a locale-shaped token names: "en-ca" → ca, "ca_fr" → ca, "gb" → gb. */
function countryOfLocaleToken(token: string, allowBare: boolean): string | null {
  const pair = /^([a-z]{2})[-_]([a-z]{2})$/.exec(token);
  if (pair) {
    const [, first, second] = pair;
    // "en-ca", "fr-ca", "de-at": language first. "ca-fr", "ca_en": country first.
    const country = LANGUAGE_CODES.has(first) ? second : first;
    return KNOWN_COUNTRIES.has(country) ? country : null;
  }
  if (allowBare && (BARE_COUNTRY_MARKERS.has(token) || token === "eu")) return token;
  return null;
}

/**
 * What a URL's subdomains or leading path segments say about its market.
 * `country`: an unambiguous sign (`en-ca`, `fr_fr`, `GB_en`, `/uk/`, `ca.`).
 * `language`: the home country of the first bare language code (`/fr/`,
 * `fr.`, `/de/`, `/ja/`), which counts for that country alone
 * (LANGUAGE_HOME_COUNTRY).
 * An unambiguous sign anywhere wins: `/fr/ca/` and `/ca/fr/` are Canada.
 */
function urlLocale(parsed: URL): { country: string | null; language: string | null } {
  const { subdomains, name, suffix } = hostParts(bareHost(parsed.hostname));
  // Under a brand's own top-level domain the country sits where a site's name
  // would: ca.pioneer is Pioneer Canada. Not under .com: se.com is Schneider.
  const hostLabels = suffix.length === 1 && suffix[0].length >= 4 && name.length === 2 ? [...subdomains, name] : subdomains;
  const tokens: Array<[token: string, allowBare: boolean]> = [
    ...hostLabels.map((label): [string, boolean] => [label, true]),
    ...parsed.pathname
      .toLowerCase()
      .split("/")
      .filter(Boolean)
      .slice(0, 3)
      .map((segment, i): [string, boolean] => [segment, i < 2]),
  ];
  let language: string | null = null;
  for (const [token, allowBare] of tokens) {
    const country = countryOfLocaleToken(token, allowBare);
    if (country) return { country, language };
    if (allowBare && language === null && LANGUAGE_HOME_COUNTRY[token]) language = LANGUAGE_HOME_COUNTRY[token];
  }
  return { country: null, language };
}

/**
 * The country a host's domain names ("ca" for bestbuy.ca, "uk" for
 * argos.co.uk, "co" for falabella.com.co), or null for a generic one. A
 * two-letter domain sold as a generic name (`.co`, `.io`) names no country on
 * its own, but under a registry label (`.com.co`) it is that country's own:
 * hostParts reads every two-part country suffix the same way.
 */
function domainCountry(host: string): string | null {
  const { suffix } = hostParts(host);
  const last = suffix[suffix.length - 1] ?? "";
  if (last.length !== 2) return null;
  if (suffix.length === 1 && GENERIC_TWO_LETTER_TLDS.has(last)) return null;
  return last;
}

function compact(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "");
}

const GENERIC_BRANDS = new Set(["generic", "unbranded", "unknown", "na", "none", "various", "nobrand"]);

const LEADING_WORDS = new Set(["the", "le", "la", "les", "a", "an", "l"]);

/** The brand as the product's own name says it: its first word ("Phonak" in "Phonak Audeo Lumity"). */
export function brandFromTitle(title: string | null | undefined): string {
  const words = (title ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (LEADING_WORDS.has(words[0].toLowerCase()) && words.length > 1) return `${words[0]} ${words[1]}`;
  return words[0];
}

/** Brand spellings worth looking for in a URL, compacted ("tp-link" → "tplink"), 3 characters or more. */
function brandKeys(product: { title?: string | null; brandName?: string | null }): string[] {
  const keys = new Set<string>();
  for (const raw of [product.brandName, brandFromTitle(product.title)]) {
    const key = compact(raw ?? "");
    if (key.length >= 3 && !GENERIC_BRANDS.has(key)) keys.add(key);
  }
  return [...keys];
}

/** Top-level domains that are a brand's own name (home-appliances.philips). */
export const BRAND_TLDS: ReadonlySet<string> = new Set([
  "philips", "bosch", "sony", "samsung", "google", "apple", "canon", "nikon", "lego", "dell", "audi", "bmw", "ikea",
]);

/**
 * Generic top-level domains a maker's own site sits on. With every country
 * code (two letters, and its second level: co.uk, com.au), these are the
 * "real" ones: a lookalike or a parked shop sits on .mom, .xyz, .top or a
 * pseudo-suffix like uk.com (QA review, 2026-09-28: dunelm.mom,
 * theordinary.uk.com).
 */
const MAKER_GENERIC_TLDS = new Set(["com", "net", "org", "shop", "store"]);

/**
 * The names a maker's domain may be, with the words they come from: the
 * brand's whole name and its leading words ("Lee Valley Tools" → leevalleytools,
 * leevalley, lee), and the title's first one to three words ("Jabra Enhance
 * Select 300" → jabra, jabraenhance, jabraenhanceselect).
 */
function makerNameKeys(product: { title?: string | null; brandName?: string | null }): Array<[key: string, name: string]> {
  const out: Array<[string, string]> = [];
  const add = (words: string[], maxWords: number) => {
    for (let k = Math.min(words.length, maxWords); k >= 1; k--) {
      const key = compact(words.slice(0, k).join(""));
      if (key.length >= 2 && !GENERIC_BRANDS.has(key) && !LEADING_WORDS.has(key)) out.push([key, words.slice(0, k).join(" ")]);
    }
  };
  add((product.brandName ?? "").trim().split(/\s+/).filter(Boolean), 6);
  add((product.title ?? "").trim().split(/\s+/).filter(Boolean), 3);
  return out;
}

/**
 * The brand whose own site this is, as the product writes it, or null. The
 * site's registrable name must BE the brand (ecobee.com, oticon.ca,
 * apple.com/ca/shop, store.hp.com), on a country-code or common top-level
 * domain, or the top-level domain is the brand's (home-appliances.philips).
 * A host that merely contains the brand is a reseller or a lookalike
 * (djidroner.com for DJI, canadianvitaminshop.com, dunelm.mom), never the
 * "Official site" (QA review, 2026-09-28: the old test was a substring).
 */
export function makerBrandOf(
  url: string | null | undefined,
  product: { title?: string | null; brandName?: string | null } | null | undefined,
): string | null {
  const parsed = parse(url);
  if (!parsed || !product) return null;
  const { name, suffix } = hostParts(bareHost(parsed.hostname));
  const tld = suffix[suffix.length - 1] ?? "";
  const site = compact(name);
  const realTld = tld.length === 2 || MAKER_GENERIC_TLDS.has(tld);
  for (const [key, words] of makerNameKeys(product)) {
    if (suffix.length === 1 && BRAND_TLDS.has(tld) && key === tld) return words;
    if (realTld && site && key === site) return words;
  }
  return null;
}

/** Is this link on the maker's own site (see makerBrandOf)? */
export function isMakerLink(
  url: string | null | undefined,
  product: { title?: string | null; brandName?: string | null } | null | undefined,
): boolean {
  return makerBrandOf(url, product) !== null;
}

/**
 * What we know of a store besides its URL. `pricedInMarket`: we read a price
 * in the market's currency on that very page (an offers row), which a store
 * outside the market does not quote. `makerStore`: the row is the brand's own
 * store (merchantKind 'brand'); on its own it no longer says which markets
 * the store sells to (2026-09-28), so it counts only with a price.
 */
export type StoreEvidence = { pricedInMarket?: boolean; makerStore?: boolean };

/**
 * What the URL itself says about its market: 'this' market, an 'other' one,
 * or 'none' (a generic domain with no locale). An Amazon marketplace, a
 * country domain, a .eu store, a locale in the host or the path.
 */
function marketSign(parsed: URL, code: string, countries: readonly string[]): "this" | "other" | "none" {
  const host = bareHost(parsed.hostname);
  if (isAmazonMarketHost(host)) {
    const expected = REGION_FOR_CODE[code as RegionCode] ? getAmazonDomainForRegion(code as RegionCode) : null;
    return !expected || hostMatches(host, expected) ? "this" : "other";
  }
  const locale = urlLocale(parsed);
  const serves = (country: string) => (country === "eu" ? EU_MARKETS.has(code) : countries.includes(country));
  // A .eu store serves the EU markets, unless its URL names the country
  // (panasonic.eu/GB_en/ is the UK store).
  if (host.endsWith(".eu")) return (locale.country ? serves(locale.country) : EU_MARKETS.has(code)) ? "this" : "other";
  const tld = domainCountry(host);
  if (tld) return countries.includes(tld) ? "this" : "other";
  if (locale.country) return serves(locale.country) ? "this" : "other";
  // A bare `/fr/` or `fr.` is France's sign; elsewhere it decides nothing.
  if (locale.language && countries.includes(locale.language)) return "this";
  return "none";
}

/**
 * Does the store behind this URL serve the market? Unknown markets and
 * affiliate click hosts pass. See the file header for the rules.
 */
export function linkServesMarket(
  url: string | null | undefined,
  regionCode: string | null | undefined,
  _product?: { title?: string | null; brandName?: string | null },
  evidence: StoreEvidence = {},
): boolean {
  const code = (regionCode ?? "").toUpperCase();
  const countries = MARKET_COUNTRIES[code];
  const parsed = parse(url);
  if (!countries || !parsed) return true;
  const host = bareHost(parsed.hostname);
  if (!host || TRACKING_HOST_SET.has(host)) return true;
  if (isAmazonShortHost(host)) return true;

  const sign = marketSign(parsed, code, countries);
  if (sign !== "none") return sign === "this";

  // A generic domain whose page quoted this market's currency serves the
  // market (its URL names no other).
  if (evidence.pricedInMarket) return true;

  const merchant = lookupOfferMerchant(host);
  if (merchant) return merchant.regions.includes(code);
  if ((LOCAL_GENERIC_RETAILERS[code] ?? []).some((domain) => hostMatches(host, domain))) return true;

  // A generic domain with no sign of any market: an American store. The
  // maker's own site included, unless a price says otherwise: until
  // 2026-09-28 a maker's site served "every market it does not rule out",
  // which is how jabraenhance.com (sold in the US only) became "Our pick" on
  // a Canadian page.
  return code === "US";
}

/**
 * Does the URL itself name a market other than this one (another Amazon
 * marketplace, another country's domain or locale, an EU store on a
 * non-EU page)? A generic domain with no locale names none.
 */
export function urlNamesOtherMarket(url: string | null | undefined, regionCode: string | null | undefined): boolean {
  const code = (regionCode ?? "").toUpperCase();
  const countries = MARKET_COUNTRIES[code];
  const parsed = parse(url);
  if (!countries || !parsed) return false;
  const host = bareHost(parsed.hostname);
  if (!host || TRACKING_HOST_SET.has(host) || isAmazonShortHost(host)) return false;
  return marketSign(parsed, code, countries) === "other";
}

/**
 * Path segments of a store's shopping pages: /shop/, /store/, /buy/, /cart/,
 * /checkout/, /p/ (apple.com/ca/shop/buy-iphone, lenovo.com/ca/en/p/...). Not a
 * bare /product(s)/: on the 1-in-16 sample it admitted 142 more maker pages,
 * about half of them manufacturers' catalogues (ESAB, Lincoln Electric,
 * Weidmüller, Tyrolit, pro.sony, AMD, Lindab), as the review warned.
 */
const SHOP_SEGMENTS = new Set(["shop", "store", "buy", "cart", "checkout", "p"]);

/**
 * A shopping path that goes past the store's landing page: a shopping segment
 * and at least one other segment that is not a locale (apple.com/ca/shop/buy-iphone/…,
 * samsung.com/ca/smartphones/galaxy-s26/buy/). A bare /ca/shop/, /uk/en/store
 * or /en_FR/shop/ is the store's front door, not a page that sells this
 * product (2026-09-28 review; nonShopWord calls it a home page).
 */
function hasShopSegment(parsed: URL): boolean {
  const segments = parsed.pathname.toLowerCase().split("/").filter(Boolean);
  const at = segments.findIndex((segment) => SHOP_SEGMENTS.has(segment) || /^(?:shop|buy)-/.test(segment));
  if (at < 0) return false;
  return segments.some((segment, i) => i !== at && !isLocaleSegment(segment));
}

/**
 * Evidence that the store behind a link sells in this market: an offers row
 * for the same store priced in the market's currency (what withOwnLinkPrice
 * fills the card's price from), or a reviewed merchant whose regions include
 * the market. What "Our pick" needs, beyond the page being a product page of
 * a store serving the market (2026-09-28).
 *
 * And, until the box records the price a maker's page states (QA review,
 * 2026-09-28, Olivier): the brand's own store for this market. The maker's
 * own site (makerBrandOf: its registrable name is the brand), whose address
 * names this market (a country domain, /ca/, /en-ca/, ca.) and a shopping
 * path (/shop/, /buy/, /cart/, /p/): apple.com/ca/shop/buy-iphone/...,
 * samsung.com/ca/.../buy/, lenovo.com/ca/en/p/... A home page or a support
 * page never gets here (buyLinkProblem refuses it first), and a site that is
 * not the brand's (jabraenhance.com), or names another market (ezvizlife.com/eu),
 * gives no such evidence.
 */
export function linkSellsInMarket(
  url: string | null | undefined,
  options: {
    regionCode: string | null | undefined;
    offers?: ReadonlyArray<{
      merchantDomain?: string | null;
      url?: string | null;
      kind?: string | null;
      price?: { value?: number | null; currency?: string | null } | null;
    }> | null;
    marketCurrency?: string | null;
    /** The product, for the brand's own market store (above). */
    product?: { title?: string | null; brandName?: string | null } | null;
  },
): boolean {
  const parsed = parse(url);
  if (!parsed) return false;
  const host = bareHost(parsed.hostname);
  const store = normalizeMerchantDomain(host);
  const code = (options.regionCode ?? "").toUpperCase();
  const currency = (options.marketCurrency ?? "").toUpperCase();
  const priced = (options.offers ?? []).some((offer) => {
    if (!offer || offer.kind === "info") return false;
    const value = offer.price?.value;
    if (typeof value !== "number" || !(value > 0)) return false;
    if (!currency || (offer.price?.currency ?? "").toUpperCase() !== currency) return false;
    const offerStore = normalizeMerchantDomain(offer.merchantDomain || parse(offer.url)?.hostname || "");
    return !!offerStore && !!store && offerStore === store;
  });
  if (priced) return true;
  const merchant = lookupOfferMerchant(host);
  if (merchant && code && merchant.regions.includes(code)) return true;
  const countries = MARKET_COUNTRIES[code];
  return (
    !!options.product &&
    !!countries &&
    makerBrandOf(parsed.href, options.product) !== null &&
    marketSign(parsed, code, countries) === "this" &&
    hasShopSegment(parsed)
  );
}

/** An Amazon product link on another market's marketplace (amazon.com on an en-CA page). */
export function isOffMarketAmazonLink(url: string | null | undefined, regionCode: string | null | undefined): boolean {
  const parsed = parse(url);
  if (!parsed) return false;
  const host = bareHost(parsed.hostname);
  return isAmazonMarketHost(host) && !linkServesMarket(url, regionCode);
}

/* ------------------------------------------------------------------ *
 * The product
 * ------------------------------------------------------------------ */

/** Path words that describe no product: shop furniture, file suffixes, locales. */
const NON_DESCRIPTIVE_WORDS = new Set([
  "products", "product", "item", "items", "itm", "html", "htm", "php", "aspx", "shop", "store", "www",
  "buy", "catalog", "catalogue", "detail", "details", "sku", "index", "view", "collections", "collection",
  "produit", "produits", "p", "dp", "en", "fr", "de", "es", "it",
]);

/**
 * Does the link's page name this product? False when it names another model
 * of the line, or when its path is a descriptive slug (three words or more)
 * with neither the product's brand nor any of its model codes. A link whose
 * path says nothing (`/itm/306865716099`) cannot be judged and passes.
 */
export function linkNamesProduct(
  url: string | null | undefined,
  product: { title?: string | null; brandName?: string | null },
): boolean {
  const parsed = parse(url);
  if (!parsed || !product?.title) return true;
  if (linkNamesOtherModel(parsed.href, { title: product.title })) return false;
  let path = parsed.pathname;
  try {
    path = decodeURIComponent(path);
  } catch {
    // keep the raw path
  }
  const host = bareHost(parsed.hostname);
  const whole = compact(`${host} ${path}`);
  const brands = brandKeys(product);
  if (brands.some((key) => whole.includes(key))) return true;
  const pathTokens = productTokens(path);
  const models = productTokens(product.title).filter(isModelCode);
  if (models.some((code) => pathTokens.includes(code) || compact(path).includes(code))) return true;
  const descriptive = pathTokens.filter((token) => /^[a-z]{3,}$/.test(token) && !NON_DESCRIPTIVE_WORDS.has(token));
  return descriptive.length < 3 || brands.length === 0;
}

/**
 * Does the link's page name this product on its own evidence: the product's
 * brand in its host or path, or one of the title's model codes in its path?
 * Unlike linkNamesProduct, a path that says nothing is not taken on trust.
 * For a page found by other means than a buy-link search (the page a product
 * photo came from), which has to earn the button.
 */
export function linkNamesProductStrictly(
  url: string | null | undefined,
  product: { title?: string | null; brandName?: string | null },
): boolean {
  const parsed = parse(url);
  if (!parsed || !product?.title) return false;
  if (linkNamesOtherModel(parsed.href, { title: product.title })) return false;
  let path = parsed.pathname;
  try {
    path = decodeURIComponent(path);
  } catch {
    // keep the raw path
  }
  const host = bareHost(parsed.hostname);
  const whole = compact(`${host} ${path}`);
  if (brandKeys(product).some((key) => whole.includes(key))) return true;
  const pathTokens = productTokens(path);
  const models = productTokens(product.title).filter(isModelCode);
  return models.some((code) => pathTokens.includes(code) || compact(path).includes(code));
}

/* ------------------------------------------------------------------ *
 * The verdict
 * ------------------------------------------------------------------ */

export type BuyLinkProblem = "not-a-product-page" | "other-market" | "other-product";

/**
 * Why this link may not be a buy button on this page, or null when it may.
 * `info: true` is an information row (a brand's official page, no price):
 * only the product-page rule applies to it, since it sells nothing.
 */
export function buyLinkProblem(
  url: string | null | undefined,
  options: {
    regionCode: string | null | undefined;
    product?: { title?: string | null; brandName?: string | null };
    info?: boolean;
    evidence?: StoreEvidence;
  },
): BuyLinkProblem | null {
  const parsed = parse(url);
  if (!parsed) return "not-a-product-page";
  const host = bareHost(parsed.hostname);
  const amazon = isAmazonMarketHost(host) || isAmazonShortHost(host);
  if (!amazon && !TRACKING_HOST_SET.has(host) && nonShopWord(parsed.href) !== null) return "not-a-product-page";
  if (options.info) return null;
  if (!linkServesMarket(parsed.href, options.regionCode, options.product, options.evidence)) return "other-market";
  if (!amazon && options.product && !linkNamesProduct(parsed.href, options.product)) return "other-product";
  return null;
}

/**
 * What an offers row says about its store: priced in the market's currency
 * (`marketCurrency`, e.g. "CAD"), or the brand's own store.
 */
export function offerStoreEvidence(
  offer: { price?: { currency?: string | null } | null; merchantKind?: string | null },
  marketCurrency: string | null | undefined,
): StoreEvidence {
  const currency = offer.price?.currency;
  return {
    pricedInMarket:
      typeof currency === "string" && !!marketCurrency && currency.toUpperCase() === marketCurrency.toUpperCase(),
    makerStore: offer.merchantKind === "brand",
  };
}

/* ------------------------------------------------------------------ *
 * Third-party parameters
 * ------------------------------------------------------------------ */

/**
 * Query parameters that credit someone else or only track an ad click: eBay
 * Partner Network (campid, customid, mkevt, mkcid, mkrid, toolid), ad click
 * ids (msclkid, gclid, fbclid and the like), Microsoft and Google Shopping
 * campaign fields, and billiger's campaign fields. Our own tags are never
 * here: Amazon's `tag`, `linkCode` and `AssociateTag`, and a network's
 * tracking URL, which /go takes verbatim from the encrypted token.
 */
const THIRD_PARTY_PARAMS = new Set([
  "campid", "customid", "mkevt", "mkcid", "mkrid", "mksid", "toolid",
  "msclkid", "gclid", "gclsrc", "gbraid", "wbraid", "dclid", "fbclid", "ttclid", "twclid", "yclid",
  "li_fat_id", "igshid", "mc_cid", "mc_eid", "_ga", "_gl", "srsltid", "gad_source", "gad_campaignid",
  "adid", "campaign", "campaignid", "audsrc", "irclickid", "cjevent", "cjdata", "ef_id", "s_kwcid",
  "ranmid", "ransiteid", "raneaid", "mscampaign", "msadgroup", "msdevice", "mstargetid", "msmatch",
  "msnetwork", "mskeyword", "msadid", "em_src", "em_cmp",
]);

function isThirdPartyParam(rawKey: string): boolean {
  let key = rawKey;
  try {
    key = decodeURIComponent(rawKey.replace(/\+/g, " "));
  } catch {
    // keep the raw key
  }
  key = key.trim().toLowerCase();
  return THIRD_PARTY_PARAMS.has(key) || key.startsWith("utm_") || key.startsWith("adgroup");
}

/**
 * The URL without third-party affiliate and ad parameters. Everything else,
 * the order and the encoding of the surviving parameters and the fragment
 * included, is kept byte for byte. A URL with nothing to strip comes back
 * as the same string.
 */
export function stripThirdPartyParams(url: string): string {
  if (typeof url !== "string") return url;
  const queryAt = url.indexOf("?");
  if (queryAt === -1) return url;
  const hashAt = url.indexOf("#", queryAt);
  const base = url.slice(0, queryAt);
  const query = url.slice(queryAt + 1, hashAt === -1 ? undefined : hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const pairs = query.split("&");
  const kept = pairs.filter((pair) => pair !== "" && !isThirdPartyParam(pair.split("=")[0] ?? ""));
  if (kept.length === pairs.length) return url;
  return `${base}${kept.length ? `?${kept.join("&")}` : ""}${hash}`;
}
